import "./ReportLineChartData.scss";
import { useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartSeriesLabel from "highcharts/modules/series-label";
import HighchartDumbbell from "highcharts/modules/dumbbell";
import HighchartLollipop from "highcharts/modules/lollipop";
import HighchartsReact, {
  HighchartsReactRefObject
} from "highcharts-react-official";
import { cloneDeep } from "lodash";
import { RootState } from "../../../store/reducers";
import { JobPricingReportConstants } from "../../../constants/JobPricingReportConstants";
import { JobPricingContext } from "../../../store/JobPricingContext";
import {
  JobPricingPercentileData,
  JobPricingPercentileDetails
  } from "../../../helpers/models/JobPricingDetailModels";
import { GradientCircularProgress } from "../../../store/slices/jobPricingDetailsSlice";
import { useTranslation } from "react-i18next";
// Load Highcharts modules
HighchartsMore(Highcharts);
HighchartSeriesLabel(Highcharts);
HighchartDumbbell(Highcharts);
HighchartLollipop(Highcharts);

Highcharts.setOptions({
  lang: {
    thousandsSep: ","
  }
});

const BLUE_LINE_CHART_COLOR = "#54A0FF";
const ORANGE_LINE_CHART_COLOR = "#FFC759";
const PURPLE_LINE_CHART_COLOR = "#8338EB";

export interface CustomLegendItemObject extends Highcharts.LegendItemObject {
  label: Highcharts.SVGElement;
  customCircle: Highcharts.SVGElement;
}

export const ReportLineChartData = () => {
  const { t } = useTranslation();
  const { setLineChartReference } = useContext(JobPricingContext);
  const {
    targetData,
    reportDetails,
    showSelectedPercentileList,
    isReportDetailsLoading
  } = useSelector((state: RootState) => state.jobPricingDetails);
  const LINE_CHART_OPTIONS = {
    chart: {
      type: "spline",
      height: "60%"
    },
    title: {
      text: ""
    },
    credits: {
      enabled: false
    },
    lang: {
      noData: JobPricingReportConstants.NO_DATA_AVAILABLE_MSG
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      reversed: true,
      padding: 15,
      itemDistance: 30,
      itemMarginBottom: 15,
      symbolHeight: 10,
      symbolWidth: 10,
      useHTML: true,
      events: {
        itemClick: function () {
          return false;
        }
      },
      labelFormatter: function () {
        return formatCustomLengend(this);
      }
    },
    xAxis: {
      title: {
        text: t("pa.percentileTitle")
      },
      labels: {
        style: {
          fontWeight: "bold"
        }
      },
      categories: JobPricingReportConstants.DEFAULT_PERCENTILE_LABELS
    },
    yAxis: {
      title: {
        text: ""
      },
      labels: {
        style: {
          fontWeight: "bold"
        }
      },
      gridLineWidth: 0,
      lineWidth: 1
    },
    plotOptions: {
      spline: {
        dashStyle: "ShortDot",
        marker: {
          symbol: "circle"
        },
        states: {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        }
      },
      lollipop: {
        dashStyle: "ShortDot",
        marker: {
          symbol: "circle"
        },
        states: {
          hover: {
            enabled: false
          },
          inactive: {
            enabled: false
          }
        }
      }
    },
    series: [],
    exporting: {
      enabled: false,
      chartOptions: {
        title: {
          text: t("pa.compensationTitle") + " " + t("pa.byPercentile")
        },
        chart: {
          width: 500,
          height: 370,
          style: {
            fontFamily: "Lato",
            fontSize: "16px"
          }
        },
        legend: {
          itemStyle: {
            fontSize: "16px"
          }
        }
      }
    }
  } as Highcharts.Options;
  const [lineChartOptions, setLineChartOptions] =
    useState<Highcharts.Options>(LINE_CHART_OPTIONS);
  const lineChartComponent = useRef<HighchartsReactRefObject>(null);
  const [primaryCompElement, setPrimaryCompElement] = useState<string>("");

  const formatCustomLengend = (
    context: Highcharts.Point | Highcharts.Series
  ) => {
    let customValue = "";
    let customLabel = "";
    if (context.options && context.options.custom) {
      customValue = context.options.custom["value"] || "";
      customLabel = context.options.custom["label"] || "";
    }
    return `<span class="line-chart-legend-item-custom">
      <span>${customLabel}</span>
      <span><b>${customValue}</b></span>
    </span>`;
  };

  const getUpdatedXAxisLabels = () => {
    const xAxisLabels =
      showSelectedPercentileList
        .filter(
          (data: JobPricingPercentileData) =>
            data.percentile !==
            JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
        )
        .map((data: JobPricingPercentileData) => data.percentileDisplay) ||
      cloneDeep(JobPricingReportConstants.DEFAULT_PERCENTILE_LABELS);
    if (!xAxisLabels.includes(targetData.percentileDisplay)) {
      xAxisLabels.push(targetData.percentileDisplay);
      if (
        targetData.percentileDisplay !==
        JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_LABELS
      ) {
        xAxisLabels.sort();
      }
    }
    return xAxisLabels;
  };

  const getUpdatedXAxisValues = () => {
    const xAxisValues =
      showSelectedPercentileList
        .filter(
          (data: JobPricingPercentileData) =>
            data.percentile !==
            JobPricingReportConstants.DEFAULT_AVERAGE_PERCENTILE_VALUES
        )
        .map((data: JobPricingPercentileData) => data.percentile) ||
      cloneDeep(JobPricingReportConstants.DEFAULT_PERCENTILE_VALUES);
    if (!xAxisValues.includes(targetData.percentile)) {
      xAxisValues.push(targetData.percentile);
      xAxisValues.sort();
    }
    return xAxisValues;
  };

  const setChartSeriesOption = () => {
    const chartPercentileValues = getUpdatedXAxisValues();
    const targetIndex = chartPercentileValues.indexOf(targetData.percentile);
    const reportData = reportDetails[0];
    if (
      reportData &&
      reportData.percentileDetails &&
      reportData.percentileDetails.length
    ) {
      setPrimaryCompElement(reportData.factName);
      const percentileDetails = reportData.percentileDetails?.filter(
        (data: JobPricingPercentileDetails, index: number) =>
          chartPercentileValues.includes(data.percentileValue)
      );
      let marketData = percentileDetails.map(
        (data: JobPricingPercentileDetails, index: number) =>
          Number(data.marketValue) || null
      );
      let orgData = percentileDetails.map(
        (data: JobPricingPercentileDetails, index: number) =>
          Number(data.clientValue) || null
      );
      const seriesData = [
        {
          type: "spline",
          name: reportData.factName,
          color: BLUE_LINE_CHART_COLOR,
          data: marketData,
          label: {
            enabled: false
          },
          showInLegend: false
        } as Highcharts.SeriesSplineOptions,
        {
          type: "spline",
          name: JobPricingReportConstants.MY_ORG,
          color: ORANGE_LINE_CHART_COLOR,
          data: orgData,
          custom: {
            value: Number(reportData.avgOrg).toLocaleString(),
            label: JobPricingReportConstants.MY_ORG_AVG
          },
          legendSymbol: "rectangle",
          label: {
            enabled: false
          },
          showInLegend: reportData.avgOrg ? true : false
        } as Highcharts.SeriesSplineOptions,
        {
          type: "lollipop",
          name: JobPricingReportConstants.TARGET,
          color: PURPLE_LINE_CHART_COLOR,
          data: [{ x: targetIndex, y: marketData[targetIndex] || null }],
          marker: {
            lineWidth: 2,
            fillColor: BLUE_LINE_CHART_COLOR,
            lineColor: PURPLE_LINE_CHART_COLOR
          },
          custom: {
            value: reportData.targetMarket
              ? Number(reportData.targetMarket).toLocaleString()
              : "-",
            label: JobPricingReportConstants.TARGET_MARKET
          },
          dataLabels: {
            enabled: true,
            align: "left",
            verticalAlign: "top",
            style: {
              color: PURPLE_LINE_CHART_COLOR
            },
            format: JobPricingReportConstants.TARGET
          },
          legendSymbol: "rectangle",
          enableMouseTracking: false
        } as Highcharts.SeriesLollipopOptions
      ];
      lineChartOptions.series = seriesData;
      lineChartOptions.exporting = {
        ...lineChartOptions.exporting,
        chartOptions: {
          ...(lineChartOptions.exporting || {}).chartOptions,
          title: {
            text: `${reportData.factName} ${t("pa.byPercentile")}`
          }
        }
      };
    }
  };

  useEffect(() => {
    if (lineChartComponent && lineChartComponent.current) {
      const lineChartRef = lineChartComponent.current.chart;
      setLineChartReference(lineChartRef);
    }
  }, [lineChartComponent, lineChartOptions]);

  useEffect(() => {
    lineChartOptions.xAxis = {
      ...lineChartOptions.xAxis,
      categories: getUpdatedXAxisLabels()
    };
    if (reportDetails && reportDetails.length) {
      setChartSeriesOption();
    } else {
      setPrimaryCompElement(t("pa.compensationTitle"));
      lineChartOptions.series = [];
    }
    setLineChartOptions({ ...lineChartOptions });
  }, [reportDetails, targetData, showSelectedPercentileList]);

  return isReportDetailsLoading ? (
    <GradientCircularProgress />
  ) : (
    <div className="sjpr-linechart-data-container">
      <Typography variant="body2" sx={{ textAlign: "center" }}>
        {primaryCompElement ? primaryCompElement : t("pa.compensationTitle")}{" "}
        {t("pa.byPercentile")}
      </Typography>
      {reportDetails && reportDetails.length > 0 ? (
        <div className="line-chart-container">
          <HighchartsReact
            highcharts={Highcharts}
            options={lineChartOptions}
            ref={lineChartComponent}
          />
        </div>
      ) : (
        <div className="chart-no-data">
          <Typography variant="body2">
            {JobPricingReportConstants.NO_DATA_AVAILABLE_MSG}
          </Typography>
        </div>
      )}
    </div>
  );
};
